import React, { useEffect, useState, useContext } from 'react';

import { Typography, Box, Grid2 as Grid, Container, CircularProgress } from '@mui/material';

import Train from './components/Train';
import { OccupancyState } from './components/icons/Occupancy';

import { Context } from './Context';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "./firebase";
import { normalizeCars } from "./utils/normalizeCars";

const useClock = () => {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);
    return now;
};

type Dataset = {
    line: {
        number: number,
        route: string
    }
    live: {
        station: string,
        time: string,
        delay: number,
        occupancy: number,
        passengers: number,
        cars: OccupancyState[][]
    }
    prognoses: {
        station: string,
        time: string,
        delay: number,
        occupancy: number,
        passengers: number,
        cars: OccupancyState[][]
    }[]
}


const LiveView = () => {
    const now = useClock();
    const timeString = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const dateString = now.toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const [dataset, setDataset] = useState<null | Dataset>(null);
    const [loading, setLoading] = useState(false);
    const theme = useTheme();
    const hideScroll = useMediaQuery(theme.breakpoints.up('lg'));

    const { selected } = useContext(Context);

    useEffect(() => {
        if (!selected) {
            setDataset(null);
            setLoading(false);
            return;
        }
        console.log("selected =", selected);

        setLoading(true);
        const { lineId, routeId } = selected;

        const ref = doc(db, "lines", lineId, "routes", routeId);
        const unsub = onSnapshot(ref, (snap) => {
            if (!snap.exists()) {
                setDataset(null);
                setLoading(false);
                return;
            }

            const raw: any = snap.data();

            const patched: Dataset = {
                ...raw,
                live: raw?.live
                    ? { ...raw.live, cars: normalizeCars(raw.live.cars) as any }
                    : raw.live,
                prognoses: Array.isArray(raw?.prognoses)
                    ? raw.prognoses.map((p: any) => ({
                        ...p,
                        cars: normalizeCars(p?.cars) as any,
                    }))
                    : [],
            };

            setDataset(patched);
            setLoading(false);
        },
            (err) => {
                console.error("❌ route onSnapshot error", err);
                setLoading(false);
            }
        );

        return () => unsub();
    }, [selected]);


    // Loading state
    if (loading) {
        return (
            <Container sx={{ height: 894, width: 710, boxShadow: 3, borderRadius: 2, margin: "0 auto", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box textAlign="center">
                    <CircularProgress sx={{ color: '#18364a' }} />
                    <Typography fontSize={15} color="#888" mt={2}>Daten werden geladen…</Typography>
                </Box>
            </Container>
        );
    }

    // No route selected state
    if (!selected) {
        return (
            <Container sx={{ height: 894, width: 710, boxShadow: 3, borderRadius: 2, margin: "0 auto", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography fontSize={15} color="#888">Keine aktiven Züge verfügbar.</Typography>
            </Container>
        );
    }

    // No data for selected route
    if (!dataset) {
        return (
            <Container sx={{ height: 894, width: 710, boxShadow: 3, borderRadius: 2, margin: "0 auto", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography fontSize={15} color="#888">Keine Daten für diesen Zug.</Typography>
            </Container>
        );
    }
    const clockBar = (
        <Box sx={{ width: 710, margin: '0 auto 12px', display: 'flex', justifyContent: 'flex-end' }}>
            <Typography fontSize={13} color="#666">
                {dateString} · {timeString} <span style={{ color: '#aaa' }}>(Ortszeit Deutschland)</span>
            </Typography>
        </Box>
    );

    return (
        <>
            {clockBar}
            <Container sx={{ height: 894, width: 710, boxShadow: 3, borderRadius: 2, margin: "0 auto", overflowY: 'hidden' }}>

            <Grid container spacing={2} sx={{ paddingTop: '34px' }}>
                <Grid size={2}>
                    <Typography fontSize={18.5} fontWeight={'bold'} align="left">Live:</Typography>
                </Grid>
                <Grid size={3} offset={1}>
                    <Typography fontSize={18.5} align="right">
                        <span style={{ fontWeight: 'bold' }}>Zug</span>: {dataset.line?.number || ''}
                    </Typography>
                </Grid>
                <Grid size="grow">
                    <Typography fontSize={18.5} align="right">{dataset.line?.route}</Typography>
                </Grid>
            </Grid>

            <Box border="2px solid #dddddd" borderRadius={'3px'} padding={'0px 10px 5px 6px'} marginTop="34px">
                <Train
                    sx={{ paddingTop: "0px" }}
                    cars={dataset.live?.cars ?? []}
                    station={dataset.live?.station ?? ''}
                    passengers={dataset.live?.passengers ?? 0}
                    time={dataset.live?.time ?? ''}
                    delay={dataset.live?.delay ?? 0}
                    occupancy={dataset.live?.occupancy ?? 0}
                />
            </Box>

            <Typography fontSize={18.5} fontWeight={'bold'} align="left" marginTop="34px">Prognosen</Typography>
            <Box style={{ overflow: 'hidden', position: 'relative' }}>
                <Box style={{ overflow: 'auto', height: '665px', width: hideScroll ? '665px' : '100%' }}>
                {dataset.prognoses.map((data, index) => (
                    <Box key={`${data.station}-${data.time}-${index}`} padding={'0px 5px 0px 5px'}>
                        <Train sx={{}} station={data.station} time={data.time} delay={data.delay} occupancy={data.occupancy} passengers={data.passengers} cars={data.cars} />
                    </Box>
                ))}

                {dataset.prognoses.length < 7 && (
                    <Box padding={'0px 5px 0px 5px'} style={{ width: '100%', height: (100 + 100 * (7 - dataset.prognoses.length)) }} />
                )}
                <Box padding={'0px 5px 0px 5px'} style={{ width: '100%', height: 100 }} />
                </Box>
            </Box>

            </Container>
        </>
    );
};

export default LiveView;